import { ApprovalState, IncidentDecisionState, IncidentNotificationState, IncidentStatus, SharedWorkflowStateValue, ToolProviderKind } from "@no-busy-work/ops-bridge-protocol";
import type { IncidentDetail } from "@no-busy-work/ops-bridge-protocol";
import { TAB_ID } from "./OpsCenter.Types";
import type { AiQuestionKind, GraphMode, OpsActionEndpointKind, TabId, UiTone } from "./OpsCenter.Types";

export const AUTH_PROVIDER_DISABLED = "disabled";
export const PREVIEW_FIXTURE_FALLBACK_MODE = "preview-default";
export const SOURCE_ARTIFACT = "ops-center-frontend";
export const OPERATOR_ACTOR_FALLBACK = "ops-center-browser";

export const UI_TONE = {
  Ok: "ok",
  Warn: "warning",
  Danger: "danger",
  Neutral: "neutral",
  Critical: "critical"
} as const satisfies Record<string, UiTone>;

export const GRAPH_MODE = {
  Incident: "incident",
  Fleet: "fleet"
} as const satisfies Record<string, GraphMode>;

export const GRAPH_NODE_KIND = {
  Incident: GRAPH_MODE.Incident,
  Service: "service",
  Route: "route",
  Provider: "provider",
  CustomerSegment: "customer-segment",
  Owner: "owner"
} as const;

export const GRAPH_NODE_ID_PREFIX = {
  Incident: `${GRAPH_NODE_KIND.Incident}:`,
  Service: `${GRAPH_NODE_KIND.Service}:`,
  Route: `${GRAPH_NODE_KIND.Route}:`,
  Provider: `${GRAPH_NODE_KIND.Provider}:`,
  CustomerSegment: `${GRAPH_NODE_KIND.CustomerSegment}:`,
  Owner: `${GRAPH_NODE_KIND.Owner}:`
} as const;

export const GRAPH_EDGE_ID_PREFIX = "edge:";

export const GRAPH_EDGE_LABEL = {
  Observes: "observes",
  Routes: "routes",
  Recovers: "recovers",
  Owns: "owns",
  Impacts: "impacts"
} as const;

export const CUSTOMER_IMPACT_STATE = {
  Impacted: "impacted",
  Monitoring: IncidentStatus.Monitoring,
  Recovered: "recovered"
} as const satisfies Record<string, IncidentDetail["customerImpact"][number]["state"]>;

export const CUSTOMER_FILTER_ALL = "all";

export const CUSTOMER_COHORT_FILTER = {
  All: CUSTOMER_FILTER_ALL,
  Complaints: "complaints",
  CustomerNotified: "customer_notified",
  ResolutionNotified: "resolution_notified",
  DeliveryRisk: "delivery_risk"
} as const;

export const OUTAGE_NOTICE_STATE = {
  Draft: "draft",
  Posted: "posted",
  NotRequired: IncidentNotificationState.NotRequired
} as const satisfies Record<string, IncidentDetail["communicationSummary"]["outageNoticeState"]>;

export const INCIDENT_NOTIFICATION_AUDIENCE_REF = {
  Owner: "owner",
  OwnerPrefix: "owner:",
  StatusPage: "status-page"
} as const;

export const OPS_ACTION_ENDPOINT_KIND = {
  Preview: "preview",
  ApprovalRequest: "approval_request",
  CodeBridgeSession: "code_bridge_session"
} as const satisfies Record<string, OpsActionEndpointKind>;

export const WORKFLOW_STATUS = {
  Approved: SharedWorkflowStateValue.Approved,
  Blocked: SharedWorkflowStateValue.Blocked,
  Executed: SharedWorkflowStateValue.Executed
} as const;

export const WORKFLOW_CLEAR_STATES = new Set<string>([
  WORKFLOW_STATUS.Approved,
  WORKFLOW_STATUS.Executed,
  ApprovalState.NotRequired,
  IncidentDecisionState.Resolved,
  IncidentStatus.Closed,
  IncidentNotificationState.Sent,
  OUTAGE_NOTICE_STATE.Posted,
  IncidentDecisionState.Acknowledged
]);

export const CLOSEOUT_GATE = {
  HealthVerification: "health-verification",
  RemediationProof: "remediation-proof",
  VerificationEvidence: "verification-evidence",
  ApprovalQueue: "approval-queue",
  OutageNotice: "outage-notice",
  CustomerNotifications: "customer-notifications",
  OwnerAcknowledgement: "owner-acknowledgement",
  RootCause: "root-cause",
  ResolutionNotifications: "resolution-notifications",
  CodeBridge: "code-bridge"
} as const;

export const CLOSEOUT_GATE_ORDER = [
  CLOSEOUT_GATE.HealthVerification,
  CLOSEOUT_GATE.RemediationProof,
  CLOSEOUT_GATE.VerificationEvidence,
  CLOSEOUT_GATE.ApprovalQueue,
  CLOSEOUT_GATE.OutageNotice,
  CLOSEOUT_GATE.CustomerNotifications,
  CLOSEOUT_GATE.OwnerAcknowledgement,
  CLOSEOUT_GATE.RootCause,
  CLOSEOUT_GATE.ResolutionNotifications,
  CLOSEOUT_GATE.CodeBridge
] as const;
export const TOOL_STATE = {
  Available: "available",
  Involved: "involved"
} as const;

export const TOOL_GROUP = {
  Observe: "observe",
  Recover: "recover",
  Deploy: "deploy",
  Communicate: "communicate",
  Secrets: "secrets",
  Infra: "infra"
} as const;

export const TOOL_GROUP_ORDER = [
  TOOL_GROUP.Observe,
  TOOL_GROUP.Recover,
  TOOL_GROUP.Deploy,
  TOOL_GROUP.Communicate,
  TOOL_GROUP.Secrets,
  TOOL_GROUP.Infra
] as const;

export const TOOL_PROVIDER_KIND = ToolProviderKind;

export const TOOL_PROVIDER_GROUP: Readonly<Record<string, string>> = {
  [TOOL_PROVIDER_KIND.GitHub]: TOOL_GROUP.Infra,
  [TOOL_PROVIDER_KIND.Notification]: TOOL_GROUP.Communicate,
  [TOOL_PROVIDER_KIND.Telephony]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.StatusPage]: TOOL_GROUP.Communicate,
  [TOOL_PROVIDER_KIND.DnsCdnLoadBalancer]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.CloudVpcFirewall]: TOOL_GROUP.Infra,
  [TOOL_PROVIDER_KIND.Database]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.Observability]: TOOL_GROUP.Observe,
  [TOOL_PROVIDER_KIND.Deploy]: TOOL_GROUP.Deploy,
  [TOOL_PROVIDER_KIND.SshBastion]: TOOL_GROUP.Secrets,
  [TOOL_PROVIDER_KIND.Worker]: TOOL_GROUP.Infra,
  [TOOL_PROVIDER_KIND.Llm]: TOOL_GROUP.Observe,
  [TOOL_PROVIDER_KIND.Vault]: TOOL_GROUP.Secrets,
  [TOOL_PROVIDER_KIND.Hetzner]: TOOL_GROUP.Infra,
  [TOOL_PROVIDER_KIND.Cloudflare]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.Caddy]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.Systemd]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.Docker]: TOOL_GROUP.Deploy,
  [TOOL_PROVIDER_KIND.Postgres]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.Redis]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.Prometheus]: TOOL_GROUP.Observe,
  [TOOL_PROVIDER_KIND.Grafana]: TOOL_GROUP.Observe,
  [TOOL_PROVIDER_KIND.GitHubRunner]: TOOL_GROUP.Deploy,
  [TOOL_PROVIDER_KIND.TelnyxFreeswitch]: TOOL_GROUP.Recover,
  [TOOL_PROVIDER_KIND.Stripe]: TOOL_GROUP.Observe,
  [TOOL_PROVIDER_KIND.Resend]: TOOL_GROUP.Communicate,
  [TOOL_PROVIDER_KIND.OpenAiAzure]: TOOL_GROUP.Observe,
  [TOOL_PROVIDER_KIND.SafeBrowsing]: TOOL_GROUP.Observe,
  [TOOL_PROVIDER_KIND.Notifications]: TOOL_GROUP.Communicate,
  [TOOL_PROVIDER_KIND.Approval]: TOOL_GROUP.Communicate,
  [TOOL_PROVIDER_KIND.CodeBridge]: TOOL_GROUP.Deploy,
  [TOOL_PROVIDER_KIND.BreakGlass]: TOOL_GROUP.Secrets
};

export const TOOL_ICON_KIND = {
  Bell: "bell",
  Bot: "bot",
  Branch: "branch",
  Chart: "chart",
  Cloud: "cloud",
  Cpu: "cpu",
  Database: "database",
  Deploy: "deploy",
  Lock: "lock",
  Phone: "phone",
  Pulse: "pulse",
  Shield: "shield",
  Status: "status"
} as const;

export const BUTTON_TYPE = {
  Button: "button",
  Submit: "submit"
} as const;

export const GRAPH_HEALTH = {
  Healthy: "healthy",
  Degraded: "degraded",
  Down: "down",
  Unknown: "unknown"
} as const;

export const OPS_ACTION_ENDPOINTS: Record<OpsActionEndpointKind, string> = {
  preview: "/api/v1/ops/actions/preview",
  approval_request: "/api/v1/ops/actions/approval-requests",
  code_bridge_session: "/api/v1/ops/code-bridge/sessions"
};

export const OPERATOR_HEADERS = {
  Actor: "X-Operator-Actor",
  Tenant: "X-Operator-Tenant",
  Authority: "X-Operator-Authority"
} as const;

export const SOURCE_OPTIONS = [
  ["operator_chat", "Operator chat"],
  ["operator_voice", "Operator voice"],
  ["manual_api", "Manual report"],
  ["endpoint_check", "Endpoint check"],
  ["synthetic_check", "Synthetic check"],
  ["provider_webhook", "Provider webhook"],
  ["worker_event", "Worker event"]
] as const;

export const SEVERITY_OPTIONS = [
  ["sev0", "SEV0"],
  ["sev1", "SEV1"],
  ["sev2", "SEV2"],
  ["sev3", "SEV3"],
  ["sev4", "SEV4"]
] as const;

export const APP_OPTIONS = [
  ["nbw", "No Busy Work"],
  ["apps", "NBW Apps"]
] as const;

export const APP_ENVIRONMENT_OPTIONS = {
  nbw: [
    ["localhost", "Localhost"],
    ["dev", "Dev"],
    ["sandbox", "Sandbox"],
    ["demo", "Demo"],
    ["production", "Production"]
  ],
  apps: [
    ["localhost", "Localhost"],
    ["sandbox", "Sandbox"],
    ["preview", "Preview"]
  ]
} as const;

export const CUSTOM_SERVICE_OPTION = "custom";

export const SERVICE_OPTIONS_BY_APP = {
  nbw: [
    ["communications.phone_lines", "Phone lines"],
    ["provider.email_delivery", "Email delivery"],
    ["database.primary", "Primary database"],
    ["web.public_site", "Public website"],
    ["auth.microsoft_entra", "Microsoft Entra"],
    ["dns.cloudflare_edge", "Cloudflare edge"],
    ["infra.floating_ip", "Floating IP"],
    ["infra.vps", "VPS host"],
    ["edge.caddy", "Caddy edge"],
    ["worker.background_jobs", "Background jobs"],
    ["github.delivery", "GitHub delivery"],
    ["billing.stripe", "Stripe billing"],
    ["safe_browsing.domain", "Safe Browsing"],
    ["ops.control_plane", "Ops control plane"],
    [CUSTOM_SERVICE_OPTION, "Custom touch point"]
  ],
  apps: [
    ["apps.database", "Apps database"],
    ["apps.api", "Apps API"],
    ["apps.web", "Apps web"],
    ["apps.middleware", "Request middleware"],
    ["apps.auth", "Apps auth"],
    ["apps.worker", "Apps worker"],
    ["apps.model_runtime", "Model runtime"],
    ["apps.notifications", "Notifications"],
    [CUSTOM_SERVICE_OPTION, "Custom touch point"]
  ]
} as const;

export const LOCALE_OPTIONS = [
  ["en", "English"],
  ["es", "Spanish"],
  ["ru", "Russian"]
] as const;

export const AUDIENCE_OPTIONS = [
  ["affected-customers", "Affected customers"],
  ["internal-operators", "Internal operators"],
  ["status-page", "Status page"],
  ["owner", "Service owner"]
] as const;

export const OWNER_OPTIONS = [
  ["employee:maya-chen", "Maya Chen"],
  ["employee:alex-morgan", "Alex Morgan"],
  ["employee:priya-shah", "Priya Shah"],
  ["employee:omar-nash", "Omar Nash"],
  ["employee:sofia-rivera", "Sofia Rivera"],
  ["owner:voice", "Voice operations"],
  ["owner:growth", "Growth"],
  ["owner:platform", "Platform"],
  ["owner:infra", "Infrastructure"],
  ["owner:database", "Database"],
  ["owner:security", "Security"],
  ["owner:apps", "Apps"],
  ["owner:delivery", "Delivery"],
  ["owner:ops", "Ops"]
] as const;

export const TAB_ORDER = [
  TAB_ID.Overview,
  TAB_ID.Impact,
  TAB_ID.Timeline,
  TAB_ID.Actions,
  TAB_ID.Comms,
  TAB_ID.Evidence,
  TAB_ID.Resolution
] as const satisfies readonly TabId[];

export const TOOL_PROVIDER_BY_TOOL_ID: Readonly<Record<string, ToolProviderKind>> = {
  tool_health_checks: TOOL_PROVIDER_KIND.Observability,
  tool_github_incident_sync: TOOL_PROVIDER_KIND.GitHub,
  tool_telephony_routing: TOOL_PROVIDER_KIND.Telephony,
  tool_customer_notification: TOOL_PROVIDER_KIND.Notification,
  tool_owner_notification: TOOL_PROVIDER_KIND.Notification,
  tool_status_page: TOOL_PROVIDER_KIND.StatusPage,
  tool_dns_cdn_load_balancer: TOOL_PROVIDER_KIND.DnsCdnLoadBalancer,
  tool_cloud_firewall: TOOL_PROVIDER_KIND.CloudVpcFirewall,
  tool_database_diagnostics: TOOL_PROVIDER_KIND.Database,
  tool_observability_logs: TOOL_PROVIDER_KIND.Observability,
  tool_deploy_rollback: TOOL_PROVIDER_KIND.Deploy,
  tool_ssh_bastion: TOOL_PROVIDER_KIND.SshBastion,
  tool_worker_sessions: TOOL_PROVIDER_KIND.Worker,
  tool_llm_recovery_advisor: TOOL_PROVIDER_KIND.Llm,
  tool_vault_secrets: TOOL_PROVIDER_KIND.Vault,
  tool_inventory_hetzner: TOOL_PROVIDER_KIND.Hetzner,
  tool_inventory_cloudflare: TOOL_PROVIDER_KIND.Cloudflare,
  tool_inventory_caddy: TOOL_PROVIDER_KIND.Caddy,
  tool_inventory_systemd: TOOL_PROVIDER_KIND.Systemd,
  tool_inventory_docker: TOOL_PROVIDER_KIND.Docker,
  tool_inventory_postgres: TOOL_PROVIDER_KIND.Postgres,
  tool_inventory_redis: TOOL_PROVIDER_KIND.Redis,
  tool_inventory_prometheus: TOOL_PROVIDER_KIND.Prometheus,
  tool_inventory_grafana: TOOL_PROVIDER_KIND.Grafana,
  tool_inventory_github_runner: TOOL_PROVIDER_KIND.GitHubRunner,
  tool_inventory_telnyx_freeswitch: TOOL_PROVIDER_KIND.TelnyxFreeswitch,
  tool_inventory_stripe: TOOL_PROVIDER_KIND.Stripe,
  tool_inventory_resend: TOOL_PROVIDER_KIND.Resend,
  tool_inventory_openai_azure: TOOL_PROVIDER_KIND.OpenAiAzure,
  tool_inventory_safe_browsing: TOOL_PROVIDER_KIND.SafeBrowsing,
  tool_inventory_notifications: TOOL_PROVIDER_KIND.Notifications,
  tool_inventory_approval: TOOL_PROVIDER_KIND.Approval,
  tool_inventory_code_bridge: TOOL_PROVIDER_KIND.CodeBridge,
  tool_inventory_vault: TOOL_PROVIDER_KIND.Vault,
  tool_inventory_break_glass: TOOL_PROVIDER_KIND.BreakGlass
};
export const TOOL_ICON_BY_TOOL_ID: Readonly<Record<string, string>> = {
  tool_health_checks: TOOL_ICON_KIND.Pulse,
  tool_github_incident_sync: TOOL_ICON_KIND.Branch,
  tool_telephony_routing: TOOL_ICON_KIND.Phone,
  tool_status_page: TOOL_ICON_KIND.Status,
  tool_inventory_hetzner: TOOL_ICON_KIND.Cloud,
  tool_inventory_cloudflare: TOOL_ICON_KIND.Cloud,
  tool_inventory_caddy: TOOL_ICON_KIND.Status,
  tool_inventory_systemd: TOOL_ICON_KIND.Cpu,
  tool_inventory_docker: TOOL_ICON_KIND.Cpu,
  tool_inventory_postgres: TOOL_ICON_KIND.Database,
  tool_inventory_redis: TOOL_ICON_KIND.Database,
  tool_inventory_prometheus: TOOL_ICON_KIND.Pulse,
  tool_inventory_grafana: TOOL_ICON_KIND.Chart,
  tool_inventory_github_runner: TOOL_ICON_KIND.Branch,
  tool_inventory_telnyx_freeswitch: TOOL_ICON_KIND.Phone,
  tool_inventory_stripe: TOOL_ICON_KIND.Status,
  tool_inventory_resend: TOOL_ICON_KIND.Bell,
  tool_inventory_openai_azure: TOOL_ICON_KIND.Bot,
  tool_inventory_safe_browsing: TOOL_ICON_KIND.Shield,
  tool_inventory_notifications: TOOL_ICON_KIND.Bell,
  tool_inventory_approval: TOOL_ICON_KIND.Shield,
  tool_inventory_code_bridge: TOOL_ICON_KIND.Branch,
  tool_inventory_vault: TOOL_ICON_KIND.Lock,
  tool_inventory_break_glass: TOOL_ICON_KIND.Lock
};
export const TOOL_ICON_BY_PROVIDER: Readonly<Record<string, string>> = {
  [TOOL_PROVIDER_KIND.GitHub]: TOOL_ICON_KIND.Branch,
  [TOOL_PROVIDER_KIND.Notification]: TOOL_ICON_KIND.Bell,
  [TOOL_PROVIDER_KIND.Telephony]: TOOL_ICON_KIND.Phone,
  [TOOL_PROVIDER_KIND.StatusPage]: TOOL_ICON_KIND.Status,
  [TOOL_PROVIDER_KIND.DnsCdnLoadBalancer]: TOOL_ICON_KIND.Cloud,
  [TOOL_PROVIDER_KIND.CloudVpcFirewall]: TOOL_ICON_KIND.Shield,
  [TOOL_PROVIDER_KIND.Database]: TOOL_ICON_KIND.Database,
  [TOOL_PROVIDER_KIND.Observability]: TOOL_ICON_KIND.Chart,
  [TOOL_PROVIDER_KIND.Deploy]: TOOL_ICON_KIND.Deploy,
  [TOOL_PROVIDER_KIND.SshBastion]: TOOL_ICON_KIND.Shield,
  [TOOL_PROVIDER_KIND.Worker]: TOOL_ICON_KIND.Cpu,
  [TOOL_PROVIDER_KIND.Llm]: TOOL_ICON_KIND.Bot,
  [TOOL_PROVIDER_KIND.Hetzner]: TOOL_ICON_KIND.Cloud,
  [TOOL_PROVIDER_KIND.Cloudflare]: TOOL_ICON_KIND.Cloud,
  [TOOL_PROVIDER_KIND.Caddy]: TOOL_ICON_KIND.Status,
  [TOOL_PROVIDER_KIND.Systemd]: TOOL_ICON_KIND.Cpu,
  [TOOL_PROVIDER_KIND.Docker]: TOOL_ICON_KIND.Cpu,
  [TOOL_PROVIDER_KIND.Postgres]: TOOL_ICON_KIND.Database,
  [TOOL_PROVIDER_KIND.Redis]: TOOL_ICON_KIND.Database,
  [TOOL_PROVIDER_KIND.Prometheus]: TOOL_ICON_KIND.Pulse,
  [TOOL_PROVIDER_KIND.Grafana]: TOOL_ICON_KIND.Chart,
  [TOOL_PROVIDER_KIND.GitHubRunner]: TOOL_ICON_KIND.Branch,
  [TOOL_PROVIDER_KIND.TelnyxFreeswitch]: TOOL_ICON_KIND.Phone,
  [TOOL_PROVIDER_KIND.Stripe]: TOOL_ICON_KIND.Status,
  [TOOL_PROVIDER_KIND.Resend]: TOOL_ICON_KIND.Bell,
  [TOOL_PROVIDER_KIND.OpenAiAzure]: TOOL_ICON_KIND.Bot,
  [TOOL_PROVIDER_KIND.SafeBrowsing]: TOOL_ICON_KIND.Shield,
  [TOOL_PROVIDER_KIND.Notifications]: TOOL_ICON_KIND.Bell,
  [TOOL_PROVIDER_KIND.Approval]: TOOL_ICON_KIND.Shield,
  [TOOL_PROVIDER_KIND.CodeBridge]: TOOL_ICON_KIND.Branch,
  [TOOL_PROVIDER_KIND.Vault]: TOOL_ICON_KIND.Lock,
  [TOOL_PROVIDER_KIND.BreakGlass]: TOOL_ICON_KIND.Lock
};
export const AI_QUESTION_KIND = {
  Triage: "triage",
  Customers: "customers",
  Notice: "notice",
  Blockers: "blockers",
  Tools: "tools",
  Runbook: "runbook",
  Fingerprint: "fingerprint",
  Approvals: "approvals",
  Evidence: "evidence",
  Comms: "comms"
} as const satisfies Record<string, AiQuestionKind>;

export const AI_SUGGESTION_KINDS = [
  AI_QUESTION_KIND.Customers,
  AI_QUESTION_KIND.Notice,
  AI_QUESTION_KIND.Blockers,
  AI_QUESTION_KIND.Tools,
  AI_QUESTION_KIND.Runbook,
  AI_QUESTION_KIND.Fingerprint
] as const satisfies readonly AiQuestionKind[];
